import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { AxiosError, AxiosResponse } from "axios";
import { Quotation } from "../../../../stores/CoursePreviewStore/Types";
import { postRequest, putRequest } from "../../../../utils/httpRequest";
import Action from "../../common/Action";
import TermsNote from "./TermsNote";
import FreeForm from "./FreeForm";
import Voucher from "./Voucher";
import {
  acceptableCountries,
  stripeStyles,
  validateFormFields,
  requestButtonStyles,
} from "../helpers";
import { PaymentType, Priceable } from "../../Types";

interface Props {
  isCurrentUserPresent: boolean;
  payUrl: string;
  activeQuote: Quotation;
  couponCode: string;
  refCode: string;
  priceable: Priceable;
  userDetails: {
    name: string;
    email: string;
  };
  params: {
    billingEmail: string;
    billingName: string;
    vatNumber: string;
    vatType: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    companyName?: string;
  };
  paymentType: PaymentType;
  slug: string;
  monthlySubsequentPayment: string;
  monthlyTotalAmount: string;
  applyVoucher: (couponCode: string, refCode: string) => Promise<void>;
  handlePaymentSuccess: (data: any) => void;
  previousAction: (e: any) => void;
  updateUserDetails: (key: string, value: string) => void;
}

// The words shown under the card fields when the payment request fails
// before Stripe answers. Always a plain sentence: the server sometimes replies
// with a JSON object rather than a string, and setting an object as the
// message used to crash the form to a blank page, which is the one outcome
// worse than the error itself.
export const SUPPORT_EMAIL = "groundcontrol@42courses.com";

// Neither sentence claims the card was or was not charged, because the form
// cannot know, but neither frightens anyone off trying again: in ten years no
// customer has been charged twice, and a scared customer is a lost sale.
const GENERIC_PAYMENT_ERROR = `Something went wrong at our end. Please try again in a moment. If it happens twice, email ${SUPPORT_EMAIL} and we will sort it out straight away.`;

const NETWORK_PAYMENT_ERROR = `We lost the connection for a moment. Check you are online and try again. If it keeps happening, email ${SUPPORT_EMAIL} and we will sort it out.`;

// A message written for a developer, not a customer: a Ruby class name, an
// exception, a stack of technical words. Those are replaced by the generic
// sentence rather than shown.
const looksTechnical = (message: string) =>
  /::|\bundefined\b|\bnil\b|NoMethodError|ActiveRecord|PG::|Stripe::|\bexception\b|\bstack\b|\bnull\b/i.test(
    message,
  );

export const describePaymentError = (error: AxiosError): string => {
  const data = error.response?.data as unknown;
  let message = "";

  if (typeof data === "string") {
    message = data;
  } else if (data && typeof data === "object") {
    const body = data as { error?: unknown; message?: unknown; errors?: unknown };
    const candidate = body.error ?? body.message ?? body.errors;
    if (typeof candidate === "string") message = candidate;
    else if (Array.isArray(candidate)) message = candidate.filter((c) => typeof c === "string").join(", ");
  }

  if (!error.response) return NETWORK_PAYMENT_ERROR;

  // "Validation failed: Email is not valid" is understandable once the
  // Rails preamble is gone, and friendlier still in our own words.
  message = message.replace(/^Validation failed:\s*/i, "").trim();
  if (/^email is not valid$/i.test(message)) {
    message = "That email address doesn't look right. Please check it and try again";
  }

  // The HTML of an error page is not a message.
  if (/<\/?[a-z][^>]*>/i.test(message)) message = "";

  const tooLongForAnError = error.response.status >= 500 && message.length > 160;
  if (!message || looksTechnical(message) || tooLongForAnError) {
    return GENERIC_PAYMENT_ERROR;
  }

  return /[.!?]$/.test(message) ? message : `${message}.`;
};

const ProcessPayment: React.FC<Props> = ({
  userDetails,
  params,
  activeQuote,
  applyVoucher,
  couponCode,
  slug,
  paymentType,
  priceable,
  refCode,

  payUrl,
  previousAction,
  handlePaymentSuccess,
  monthlySubsequentPayment,
  monthlyTotalAmount,
  updateUserDetails,
  isCurrentUserPresent,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPayRequestButton, setShowPayRequestButton] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentFailure, setPaymentFailure] = useState(false);

  useEffect(() => {
    if (stripe && acceptableCountries.includes(params.country)) {
      let pr = stripe.paymentRequest({
        country: params.country,
        currency: activeQuote?.currency_code.toLowerCase(),
        total: {
          label: "Total",
          amount: activeQuote?.fractional,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.on("paymentmethod", async (event) => {
        setSubmitting(true);
        await sendPayment(event.paymentMethod, event);
        confirmNativePayment(event);
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          setShowPayRequestButton(true);
          setPaymentRequest(pr);
        }
      });
    }
  }, [stripe]);

  const sendPayment = (paymentMethod = null, event = null) => {
    let payment = {
      quote_id: activeQuote.id,
      vat_number: params.vatNumber,
      vat_type: params.vatType,
      stripe_token: paymentMethod?.id,
      billing_email: params.billingEmail,
      billing_name: params.billingName,
      // The invoice is made out to the company; the account stays in the
      // buyer's own name.
      company_name: params.companyName || undefined,
      payer_name: userDetails?.name || undefined,
    };

    return putRequest(payUrl, { payment })
      .then((response: AxiosResponse) => {
        handleServerResponse(event, response);
      })
      .catch((error: AxiosError) => {
        setSubmitting(false);
        setError(describePaymentError(error));
      });
  };

  const confirmNativePayment = (ev) => {
    if (paymentFailure) {
      ev.complete("fail");
    } else if (paymentSuccess) {
      ev.complete("success");
    }
  };

  const upsertTempUserData = () => {
    return postRequest("/api/temp_users", {
      email: params.billingEmail,
      name: params.billingName,
    })
      .then((response: AxiosResponse) => {
        console.log("called");
      })
      .catch((error: AxiosError) => {
        console.log(error);
      });
  };

  const onClickPaymentRequestElement = (e) => {
    if (!params.billingEmail || !params.billingName) {
      alert("Please enter your name and email to proceed.");
      e.preventDefault();
    }

    upsertTempUserData();

    paymentRequest.update({
      currency: activeQuote?.currency_code.toLowerCase(),
      total: {
        label: "Total",
        amount: activeQuote?.fractional,
      },
    });
  };

  const handleServerResponse = async (event, response) => {
    const { error } = response.data;
    if (error) {
      setSubmitting(false);
      if (error.includes("Your card is not supported for this currency.")) {
        setError(
          <>
            Your card is not supported for the selected currency. Please use a
            different card or payment method or{" "}
            <Link
              to="#"
              style={{ color: "#007bff", textDecoration: "underline" }}
              onClick={(e) => changeCurrency()}
            >
              change to a different currency
            </Link>
            .
          </>,
        );
        return;
      }

      setError(error);
    } else if (response.data.requires_action) {
      event?.complete("success");
      // scrollToDiv();
      const { error: errorAction, paymentIntent } =
        await stripe.handleCardAction(
          response.data.payment_intent_client_secret,
        );

      if (errorAction) {
        setSubmitting(false);
        setPaymentFailure(true);
        setError(errorAction.message);
      } else {
        await sendPayment(paymentIntent);
      }
    } else {
      setPaymentSuccess(true);
      handlePaymentSuccess(response.data);
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setSubmitting(true);
    setError("");

    const errors = validateFormFields(params, activeQuote);

    if (errors.length > 0) {
      setSubmitting(false);
      setError(
        `${errors.join(", ")} ${errors.length > 1 ? "are" : "is"} invalid`,
      );

      return;
    }

    if (!(activeQuote?.amount > 0)) {
      // if not payment method is set, its basically for free
      sendPayment();
      return;
    }

    if (!stripe || !elements) {
      setSubmitting(false);
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    const payload = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        name: params.billingName,
        email: params.billingEmail,
        address: {
          line1: params.address,
          postal_code: params.postalCode,
          city: params.city,
          country: params.country,
        },
      },
    });

    handleResultsOfStripePaymentMethod(payload);
  };

  const changeCurrency = () => {
    $("#currency-button")[0].click();
  };

  const handleResultsOfStripePaymentMethod = (payload) => {
    if (payload.error) {
      setSubmitting(false);
      setError(payload.error.message);
    } else {
      sendPayment(payload.paymentMethod);
    }
  };

  const renderPayOptionButton = () => {
    if (showPayRequestButton && paymentRequest) {
      return (
        <div className="express-pay" id="expressPay">
          <h2 className="form-section-title">Express checkout</h2>
          <div id="express-checkout-element">
            <PaymentRequestButtonElement
              options={{
                ...requestButtonStyles,
                paymentRequest,
              }}
              onClick={(e) => onClickPaymentRequestElement(e)}
            />
          </div>
          <div className="express-divider" aria-hidden="true">
            <span>or pay by card</span>
          </div>
        </div>
      );
    }
  };

  return (
    <div>
      {activeQuote?.amount <= 0 ? (
        <FreeForm
          error={error}
          setError={setError}
          isSubmitting={submitting}
          userDetails={userDetails}
          previousAction={previousAction}
          updateUserDetails={updateUserDetails}
          handleSubmit={handleSubmit}
          isCurrentUserPresent={isCurrentUserPresent}
        />
      ) : (
        <>
          <div>
            {renderPayOptionButton()}
            <div className="field">
              <div id="card-element">
                <CardNumberElement
                  onChange={() => {}}
                  options={{ style: stripeStyles }}
                />
              </div>
              <label id="card-label">Card number</label>
            </div>
            <div className="fields">
              <div className="field">
                <div id="expiry-element">
                  <CardExpiryElement
                    onChange={() => {}}
                    options={{ style: stripeStyles }}
                  />
                </div>
                <label id="card-label">Expiry</label>
              </div>
              <div className="field">
                <div id="cvc-element">
                  <CardCvcElement
                    onChange={() => {}}
                    options={{ style: stripeStyles }}
                  />
                </div>
                <label id="card-label">CVC</label>
              </div>
            </div>
          </div>
          {error && (
            <div id="card-errors" role="alert">
              <i
                className="close icon util-clickable"
                onClick={() => setError("")}
              />
              <span>{error}</span>
            </div>
          )}
          <Voucher
            applyVoucher={applyVoucher}
            couponCode={couponCode}
            priceable={priceable}
            refCode={refCode}
          />
        </>
      )}
      <Action
        isSubmitting={submitting}
        isDisabled={submitting}
        slug={slug}
        paymentType={paymentType}
        priceable={priceable}
        activeQuote={activeQuote}
        onSubmit={(e) => handleSubmit(e)}
      />
      {slug === "community" || priceable?.is_community ? (
        <div className="guarantee-inline">
          <svg aria-hidden="true">
            <use href="#icon-shield"></use>
          </svg>
          <span id="guaranteeText">
            <strong>Cancel anytime</strong>, no lock-in, no hassle
          </span>
        </div>
      ) : (
        <div className="guarantee-inline">
          <svg aria-hidden="true">
            <use href="#icon-shield"></use>
          </svg>
          <span id="guaranteeText">
            <strong>30-day money-back guarantee</strong>, no questions asked
          </span>
        </div>
      )}
      <TermsNote
        activeQuote={activeQuote}
        monthlySubsequentPayment={monthlySubsequentPayment}
        paymentType={paymentType}
        priceable={priceable}
        slug={slug}
        monthlyTotalAmount={monthlyTotalAmount}
      />
    </div>
  );
};

export default ProcessPayment;
